import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { range, get } from 'lodash'
import { Autocomplete, Grid, TextField } from '@mui/material'
import Input from 'components/generic/form/Input'
import DatePicker from 'components/generic/form/DatePicker'
import Select from 'components/generic/form/Select'
import { CAR_BODY_TYPES, CAR_COLORS, MIN_VEHICLE_YEAR } from 'utils/constants'
import { getVehiclePlateStates } from 'utils/helpers'
import { useGetVehicleMakeModelsQuery } from 'services/vehicles'
import { Controller } from 'react-hook-form'

const getVehicleYearsArray = () => {
  const max = new Date().getUTCFullYear()
  return range(MIN_VEHICLE_YEAR, max + 1)
    .sort((a, b) => b - a)
    .map(y => ({ id: y.toString(), name: y.toString() }))
}

const VehicleFormLayout = ({ control, errors, watch, fieldPathPrefix, isTabContent, tab }) => {
  const { locale } = useSelector(state => state.auth)
  const years = getVehicleYearsArray()
  const plateStates = useMemo(() => getVehiclePlateStates(locale), [locale])
  const vehicleMake = watch(`${fieldPathPrefix}.make`)
  const { data: vehicleMakeModels } = useGetVehicleMakeModelsQuery({})
  const vehicleMakes = useMemo(
    () => (vehicleMakeModels ? Object.keys(vehicleMakeModels) || [] : []),
    [vehicleMakeModels],
  )
  const vehicleModels = useMemo(
    () => (vehicleMakeModels && vehicleMake ? vehicleMakeModels[vehicleMake?.toUpperCase() || ''] || [] : []),
    [vehicleMakeModels, vehicleMake],
  )

  return (
    <>
      {/* Plate Number and Plate State fields will be displayed on top when its customer vehicle */}
      {isTabContent && tab === 'customer-vehicle' && (
        <Grid container spacing={2} sx={{ width: isTabContent ? '75%' : '100%' }}>
          <Grid item md={isTabContent ? 8 : 6} xs={12}>
            <Input
              name={`${fieldPathPrefix}.plateNumber`}
              label="Plate Number"
              defaultValue=""
              control={control}
              error={!!get(errors, `${fieldPathPrefix}.plateNumber`)}
              helperText={get(errors, `${fieldPathPrefix}.plateNumber.message`)}
            />
          </Grid>
          <Grid item md={isTabContent ? 4 : 6} xs={6}>
            <Select
              name={`${fieldPathPrefix}.plateState`}
              label="Plate State"
              defaultValue=""
              control={control}
              items={plateStates}
              error={!!get(errors, `${fieldPathPrefix}.plateState`)}
              helperText={get(errors, `${fieldPathPrefix}.plateState.message`)}
            />
          </Grid>
        </Grid>
      )}
      <Grid container mt={1} spacing={2} sx={{ width: isTabContent ? '50%' : '100%' }}>
        <Grid item md={isTabContent ? 12 : 4} xs={12}>
          <Select
            name={`${fieldPathPrefix}.year`}
            label="Year"
            defaultValue=""
            control={control}
            items={years}
            error={!!get(errors, `${fieldPathPrefix}.year`)}
          />
        </Grid>
        <Grid item md={isTabContent ? 12 : 4} xs={12}>
          <Controller
            name={`${fieldPathPrefix}.make`}
            control={control}
            defaultValue=""
            render={({ field: { onChange, value } }) => (
              <Autocomplete
                label="Make"
                options={vehicleMakes}
                value={value}
                onChange={(e, newValue) => onChange(newValue)}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Make"
                    helperText={get(errors, `${fieldPathPrefix}.make.message`)}
                    error={!!get(errors, `${fieldPathPrefix}.make`)}
                  />
                )}
              />
            )}
          />
        </Grid>
        <Grid item md={isTabContent ? 12 : 4} xs={12}>
          <Controller
            name={`${fieldPathPrefix}.model`}
            control={control}
            defaultValue=""
            render={({ field: { onChange, value } }) => (
              <Autocomplete
                label="Model"
                options={vehicleModels}
                value={value}
                onChange={(e, newValue) => onChange(newValue)}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Model"
                    error={!!get(errors, `${fieldPathPrefix}.model`)}
                    helperText={get(errors, `${fieldPathPrefix}.model.message`)}
                  />
                )}
              />
            )}
          />
        </Grid>
      </Grid>
      <Grid container mt={1} spacing={2} sx={{ width: isTabContent ? '50%' : '100%' }}>
        <Grid item md={isTabContent ? 12 : 6} xs={12}>
          <Controller
            name={`${fieldPathPrefix}.color`}
            control={control}
            defaultValue=""
            render={({ field: { onChange, value } }) => (
              <Autocomplete
                label="Color"
                options={CAR_COLORS}
                value={value}
                onChange={(e, newValue) => onChange(newValue)}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Color"
                    helperText={get(errors, `${fieldPathPrefix}.color.message`)}
                    error={!!get(errors, `${fieldPathPrefix}.color`)}
                  />
                )}
              />
            )}
          />
        </Grid>
        <Grid item md={isTabContent ? 12 : 6} xs={12}>
          <Controller
            name={`${fieldPathPrefix}.body`}
            control={control}
            defaultValue=""
            render={({ field: { onChange, value } }) => (
              <Autocomplete
                label="Body"
                options={CAR_BODY_TYPES}
                value={value}
                onChange={(e, newValue) => onChange(newValue)}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Body"
                    helperText={get(errors, `${fieldPathPrefix}.body.message`)}
                    error={!!get(errors, `${fieldPathPrefix}.body`)}
                  />
                )}
              />
            )}
          />
        </Grid>
      </Grid>
      <Grid container mt={1} spacing={2} sx={{ width: isTabContent ? '75%' : '100%' }}>
        <Grid item md={isTabContent ? 8 : 6} xs={12}>
          <Input
            name={`${fieldPathPrefix}.titleNumber`}
            label="Title Number"
            defaultValue=""
            control={control}
            error={!!get(errors, `${fieldPathPrefix}.titleNumber`)}
            helperText={get(errors, `${fieldPathPrefix}.titleNumber.message`)}
          />
        </Grid>
        <Grid item md={isTabContent ? 4 : 6} xs={12}>
          <Select
            name={`${fieldPathPrefix}.titleState`}
            label="Title State"
            defaultValue=""
            control={control}
            items={plateStates}
            error={!!get(errors, `${fieldPathPrefix}.titleState`)}
          />
        </Grid>
      </Grid>
      {/* Plate Number and Plate State fields will be hide here when it customer vehicle */}
      {tab !== 'customer-vehicle' && (
        <Grid container mt={1} spacing={2} sx={{ width: isTabContent ? '75%' : '100%' }}>
          <Grid item md={isTabContent ? 8 : 6} xs={12}>
            <Input
              name={`${fieldPathPrefix}.plateNumber`}
              label="Plate Number"
              defaultValue=""
              control={control}
              error={!!get(errors, `${fieldPathPrefix}.plateNumber`)}
              helperText={get(errors, `${fieldPathPrefix}.plateNumber.message`)}
            />
          </Grid>
          <Grid item md={isTabContent ? 4 : 6} xs={6}>
            <Select
              name={`${fieldPathPrefix}.plateState`}
              label="Plate State"
              defaultValue=""
              control={control}
              items={plateStates}
              error={!!get(errors, `${fieldPathPrefix}.plateState`)}
            />
          </Grid>
        </Grid>
      )}
      <Grid container mt={1} spacing={2} sx={{ width: isTabContent ? '50%' : '100%' }}>
        <Grid item md={12} xs={12}>
          <DatePicker
            name={`${fieldPathPrefix}.registrationExpiration`}
            label="Registration Expiration"
            variant="outlined"
            error={!!get(errors, `${fieldPathPrefix}.registrationExpiration`)}
            helperText={get(errors, `${fieldPathPrefix}.registrationExpiration.message`)}
            control={control}
          />
        </Grid>
      </Grid>
    </>
  )
}

VehicleFormLayout.propTypes = {
  control: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  watch: PropTypes.func.isRequired,
  fieldPathPrefix: PropTypes.string,
  isTabContent: PropTypes.bool,
  tab: PropTypes.string,
}

VehicleFormLayout.defaultProps = {
  fieldPathPrefix: 'vehicle',
  isTabContent: false,
  tab: null,
}

export default VehicleFormLayout
